/**
 * Avatar Utilities
 *
 * Provides utility functions for generating user initials and deterministic
 * avatar colors for the RadixDash avatar system.
 */

/**
 * Brand color palette for avatar backgrounds
 * @constant {string[]}
 */
export const BRAND_COLORS = [
  '#007B83', // Teal Primary (index 0)
  '#00566E', // Navy Deep (index 1)
  '#008DA6', // Cyan Bright (index 2)
  '#005A7D', // Ocean Blue (index 3)
  '#00A5B8', // Turquoise (index 4)
  '#4A90A4'  // Steel Blue (index 5)
];

/**
 * Extract user initials with fallback logic
 *
 * Priority order:
 * 1. First letter of firstName + first letter of lastName
 * 2. First two letters of userName
 * 3. "RD" (RadixDash default)
 *
 * @param {Object} user - User object
 * @param {string} [user.firstName] - User's first name
 * @param {string} [user.lastName] - User's last name
 * @param {string} [user.userName] - User's username
 * @returns {string} Two-character uppercase initials
 *
 * @example
 * getInitials({ firstName: 'John', lastName: 'Doe' }) // Returns 'JD'
 * getInitials({ userName: 'jsmith' }) // Returns 'JS'
 * getInitials({}) // Returns 'RD'
 */
export const getInitials = (user) => {
  // Validate user object
  if (!user || typeof user !== 'object') {
    return 'RD';
  }

  // Primary: firstName + lastName
  if (user.firstName && user.firstName.trim() && user.lastName && user.lastName.trim()) {
    return `${user.firstName.trim()[0]}${user.lastName.trim()[0]}`.toUpperCase();
  }

  // Fallback: first two letters of userName
  if (user.userName && user.userName.trim().length >= 2) {
    const trimmed = user.userName.trim();
    return `${trimmed[0]}${trimmed[1]}`.toUpperCase();
  }

  // Ultimate fallback
  return 'RD';
};

/**
 * Simple hash function for userId
 *
 * Uses bit shifting and character code summation to generate
 * a consistent hash value for any given string.
 *
 * @param {string} userId - User ID to hash
 * @returns {number} Hash value
 * @private
 */
const hashUserId = (userId) => {
  if (!userId || typeof userId !== 'string') {
    return 0;
  }

  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

/**
 * Get deterministic background color for user avatar
 *
 * Uses a hash function to consistently map a userId to one of the
 * brand colors, ensuring the same user always gets the same color.
 *
 * @param {string} userId - User ID
 * @returns {string} Hex color code from BRAND_COLORS palette
 *
 * @example
 * getAvatarColor('user123') // Returns consistent color for this user
 * getAvatarColor('user123') // Always returns same color
 */
export const getAvatarColor = (userId) => {
  if (!userId || typeof userId !== 'string') {
    // Default to first color if userId is invalid
    return BRAND_COLORS[0];
  }

  const hash = hashUserId(userId);
  const colorIndex = hash % BRAND_COLORS.length;
  return BRAND_COLORS[colorIndex];
};
